import React, { useEffect, useState, useCallback } from 'react';
import {
    Grid,
    Typography,
    Box, List, ListItem, ListItemText,
    Button,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    CardHeader,
    CardContent,
    CardActions,
    MenuItem, Chip, Stack,
} from "@mui/material";
import { useDispatch } from "react-redux";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Loader, FormCard, Menu, Accordion} from "../../../../../../Components";
import { setModal } from "../../../../../Common/App";
import {EntityModal, deleteEntity, fetchEntities} from '../../../index';
import {cleanName} from "../../../../../../Setup/Config/formats";

export const EntityTab = () => {

    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [menuState, setMenuState] = useState({});

    // Fetch data
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchEntities({ dispatch, filters: { paginate: 'false', columns:['id','entity_name','workflow_type','display_name','module','is_template']} }).then((res) => {
            if (res.success) {
                setItems(res.data || []);
            }
        });
        setIsLoading(false);
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle menu open/close
    const handleClick = (event, id) => {
        event.stopPropagation();
        setMenuState((prev) => ({
            ...prev,
            [id]: event.currentTarget, // Set the anchor element for the specific item
        }));
    };

    const handleClose = (id) => {
        setMenuState((prev) => ({
            ...prev,
            [id]: null, // Reset the anchor element for the specific item
        }));
    };

    // Handle delete
    const handleDelete = async (e, id) => {
        e.stopPropagation();
        await deleteEntity({ dispatch, id }).then((res) => {
            if (res.success) {
                fetchData();
            }
        });
    };

    // Handle edit
    const handleEdit = (e, item) => {
        e.stopPropagation();
        dispatch(setModal({component: <EntityModal item={item} setItems={setItems} />,open: true,}));
    };

    return (
        <FormCard>
            <CardHeader
                title="Entity Settings"
                avatar={<Loader isLoading={isLoading} type={"linear"} />}
            />
            <CardContent sx={{ pb: '1rem!important' }}>
                <Grid container spacing={2}>
                    {items.length > 0 ? (
                    <Grid item xs={12}>
                        {items.map((item) => (
                            <Accordion key={item.id} rounded={0} elevation={0}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel-${item.id}-content`}
                                    id={`panel-${item.id}-header`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexDirection: 'row-reverse',
                                    }}
                                >
                                    {/* Title and Expand Icon */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                        <Typography sx={{ flexGrow: 1 }}>{`${item.entity_name} - ${item.module}`}</Typography>
                                        <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
                                            {item.is_template === true && (<Chip variant={"outlined"} color={"error"} size="small" title={item.module} label={'Template'} />)}
                                            <Chip variant={"outlined"} color={"info"} size="small" title={item.module} label={item.module} />
                                        </Stack>
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton
                                            aria-label="more"
                                            id={`menu-button-${item.id}`}
                                            aria-controls={menuState[item.id] ? `menu-${item.id}` : undefined}
                                            aria-expanded={menuState[item.id] ? 'true' : undefined}
                                            aria-haspopup="true"
                                            onClick={(e) => handleClick(e, item.id)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            id={`menu-${item.id}`}
                                            anchorEl={menuState[item.id]}
                                            open={Boolean(menuState[item.id])}
                                            onClose={() => handleClose(item.id)}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'right',
                                            }}
                                        >
                                            <MenuItem onClick={(e) => handleEdit(e, item)}>
                                                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                            </MenuItem>
                                            <MenuItem onClick={(e) => handleDelete(e, item.id)}>
                                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                                            </MenuItem>
                                        </Menu>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <List>
                                        <ListItem>
                                            <ListItemText primary="Module" secondary={cleanName(item.module)} />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Display Name" secondary={item.display_name} />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Workflow Type" secondary={cleanName(item.workflow_type)} />
                                        </ListItem>
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Grid>
                    ) : (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            No task types available.
                        </Typography>
                    </Grid>
                    )}
                </Grid>
            </CardContent>
            <CardActions>
                <Button variant="contained" size="small" color="info" onClick={() => dispatch(setModal({component: <EntityModal setItems={setItems} />, open: true,}))}>
                    + Add New
                </Button>
            </CardActions>
        </FormCard>
    );
};